import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Newspaper } from "lucide-react";
import { Heading } from "@/components/ui/heading";
import { getPosts, type GetPostsResult } from "@/lib/blog/getPosts";
import { ProgressCTA } from "@/components/home/ProgressCTA/ProgressCTA";
import { BlogCard } from "@/components/cards/blog/BlogCard";
import type { Post } from "@/lib/blog/getPost";
import { seo } from "@/utilities/seo";
import { BlogPagination } from "@/components/blog/BlogPagination";

export const Route = createFileRoute("/_home/blog/")({
  loader: async (): Promise<GetPostsResult> => {
    return await getPosts({ data: { page: 1, limit: 10 } });
  },
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: "Blog - ProgressMade.ai" },
      ...seo({
        title: "Blog - ProgressMade.ai",
        description:
          "Discover practical strategies for setting achievable health goals, building lasting habits, and integrating wellness into your busy lifestyle. Transform your approach to fitness with our evidence-based guidance.",
        keywords:
          "blog, health, fitness, wellness, planning, health habits, structure, streaks, 75 hard, Bryan Johnson",
        image: "https://progressmade.ai/meta-image.webp",
        canonical: "https://progressmade.ai/blog",
      }),
    ],
  }),
});

function RouteComponent() {
  const paginationData = Route.useLoaderData();
  const { posts, totalPages, currentPage, hasNextPage, hasPrevPage } = paginationData;

  return (
    <>
      <div className="col-span-12 md:col-span-9 md:col-start-2 xl:col-span-8 xl:col-start-3">
        <div className="flex flex-col items-center gap-4 mb-6 md:mb-12 text-center">
          <Newspaper className="h-8 w-8 text-gray-700" />

          <Heading level="h1" size="h1">
            Blog
          </Heading>

          <p className="max-w-xl text-lg text-pretty leading-8 text-foreground">
            Welcome to the <span className="text-primary">ProgressMade</span> blog! Discover
            practical strategies for setting achievable health goals, building lasting habits, and
            integrating wellness into your busy lifestyle. Transform your approach to fitness with
            our evidence-based guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map((post: Post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>

        <BlogPagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
        />
      </div>

      <ProgressCTA />
    </>
  );
}
